import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import ConfirmModal from '../components/ui/ConfirmModal';

/**
 * useConfirm() — branded async replacement for window.confirm() and
 * window.prompt(), wired through a single ConfirmModal rendered at the app
 * root by <ConfirmProvider>.
 *
 * Returns a function `confirm(opts) → Promise<{ confirmed, input? }>`.
 *
 * Mechanical swap from native dialogs:
 *
 *   // Before
 *   if (!confirm('Delete this?')) return;
 *
 *   // After
 *   const r = await confirm({ message: 'Delete this?' });
 *   if (!r.confirmed) return;
 *
 *   // window.prompt analog:
 *   const r = await confirm({
 *     title: 'Reject document',
 *     message: 'Tell the uploader why.',
 *     inputLabel: 'Reason',
 *     inputRequired: true,
 *     confirmVariant: 'danger',
 *     confirmLabel: 'Reject',
 *   });
 *   if (!r.confirmed) return;
 *   const reason = r.input;   // string
 */

const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [opts, setOpts] = useState(null);
  // Hold the resolver so the modal's button handlers can settle the promise.
  // We stash it in a ref so re-renders don't lose it.
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOpts({
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel,
        cancelLabel: options.cancelLabel,
        confirmVariant: options.confirmVariant,
        inputLabel: options.inputLabel,
        inputPlaceholder: options.inputPlaceholder,
        inputRequired: options.inputRequired,
      });
    });
  }, []);

  const settle = useCallback((result) => {
    // Capture + clear the resolver BEFORE unmounting the modal so we never
    // double-resolve (e.g. if onCancel and ESC fire in the same tick).
    const r = resolverRef.current;
    resolverRef.current = null;
    setOpts(null);
    if (r) r(result);
  }, []);

  const handleConfirm = useCallback((value) => {
    settle({ confirmed: true, input: value });
  }, [settle]);

  const handleCancel = useCallback(() => {
    settle({ confirmed: false, input: undefined });
  }, [settle]);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmCtx.Provider value={value}>
      {children}
      <ConfirmModal
        open={!!opts}
        title={opts?.title}
        message={opts?.message}
        confirmLabel={opts?.confirmLabel}
        cancelLabel={opts?.cancelLabel}
        confirmVariant={opts?.confirmVariant}
        inputLabel={opts?.inputLabel}
        inputPlaceholder={opts?.inputPlaceholder}
        inputRequired={opts?.inputRequired}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    throw new Error('useConfirm() must be called inside <ConfirmProvider>');
  }
  return ctx;
}
